import { useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Typography,
  Grid,
  FormControlLabel,
} from '@mui/material';

import { ErrorOutline } from '@mui/icons-material';
import { useAppSelector } from '../../app/hooks';
import { ShareResults } from './types';
import { grey } from '@mui/material/colors';

import { useUsers } from 'features/userManagement/api/getUsers';
import { useWalls } from 'features/walls/api/getWalls';
import { useShareWall } from 'features/walls/api/shareWall';

interface Props {
  open: boolean;
  handleClose: () => void;
  selectedWallIds: number[];
}
const ShareWallsDialog = ({ open, handleClose, selectedWallIds }: Props) => {
  const selfUserId = useAppSelector((state) => state.auth.user?.id);
  const { data: allWalls } = useWalls(selfUserId || -1);
  const { data: users } = useUsers({});
  const shareWallMutation = useShareWall({});

  const [saveOverWalls, setSaveOverWalls] = useState<boolean>(false);
  const [userIdsForSharing, setUserIdsForSharing] = useState<number[]>([]);
  const [shareResultsByUserId, setShareResultsByUserId] =
    useState<ShareResults>({});
  const [shareErrorsByUserId, setShareErrorsByUserId] = useState<ShareResults>(
    {}
  );
  const [submitted, setSubmitted] = useState(false);

  const wallsToShare = allWalls?.filter((wall) =>
    selectedWallIds.includes(wall.id)
  );
  const usersNotSelf = users?.filter((user) => user.id !== selfUserId);

  const handleUserClicked = (userId: number) => {
    if (userIdsForSharing.includes(userId))
      setUserIdsForSharing(userIdsForSharing.filter((id) => id !== userId));
    else setUserIdsForSharing((priorIds) => [...priorIds, userId]);
  };

  const handleSubmit = async () => {
    if (!wallsToShare) return;
    const newShareResults: ShareResults = {};
    const newShareErrors: ShareResults = {};
    for await (const userId of userIdsForSharing) {
      for await (const wall of wallsToShare) {
        try {
          await shareWallMutation.mutateAsync({
            wallId: wall.id,
            userId: userId,
            saveOverWall: +saveOverWalls,
          });
          if (newShareResults[userId])
            newShareResults[userId] = [...newShareResults[userId], wall.name];
          else newShareResults[userId] = [wall.name];
        } catch (err) {
          if (newShareErrors[userId])
            newShareErrors[userId] = [...newShareErrors[userId], wall.name];
          else newShareErrors[userId] = [wall.name];
        }
      }
    }
    setShareResultsByUserId(newShareResults);
    setShareErrorsByUserId(newShareErrors);
    setSubmitted(true);
  };

  const handleCloseDialog = () => {
    handleClose();
    setSubmitted(false);
    setUserIdsForSharing([]);
    setShareResultsByUserId({});
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
      <DialogTitle>Share Walls</DialogTitle>
      <DialogContent>
        <Grid container direction='column' spacing={1}>
          <Grid item>
            <Typography>
              Walls to Share:{' '}
              {wallsToShare?.map((wall) => wall.name).join(', ')}
            </Typography>
          </Grid>
          <Grid item>
            <FormControlLabel
              name='save_over_walls'
              sx={{ pl: '1rem' }}
              control={
                <Checkbox
                  checked={saveOverWalls}
                  onChange={() => setSaveOverWalls(!saveOverWalls)}
                />
              }
              label='Save over existing walls?'
            />
          </Grid>
          <Grid item>
            <Divider />
            <List>
              <ListItem>Select Users to Share Walls With:</ListItem>
              {(!usersNotSelf || usersNotSelf.length === 0) && (
                <Typography color={grey[500]} sx={{ pt: '1rem', pl: '1rem' }}>
                  No eligible users exist.
                </Typography>
              )}
              {usersNotSelf?.map((user, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <Checkbox
                      checked={userIdsForSharing.includes(user.id)}
                      edge='start'
                      tabIndex={-1}
                      onClick={() => {
                        handleUserClicked(user.id);
                      }}
                      disabled={submitted}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.name}
                    secondary={
                      submitted && (
                        <>
                          {shareResultsByUserId[user.id]?.length > 0 && (
                            <>
                              <Typography
                                component='span'
                                variant='body2'
                                color='textPrimary'
                              >
                                Sharing Succeeded:{' '}
                              </Typography>
                              {shareResultsByUserId[user.id].join(', ') + ' '}
                            </>
                          )}
                          {shareErrorsByUserId[user.id]?.length > 0 && (
                            <>
                              <Typography
                                component='span'
                                variant='body2'
                                color='textPrimary'
                              >
                                Sharing Failed:{' '}
                              </Typography>
                              {shareErrorsByUserId[user.id].join(', ')}
                            </>
                          )}
                        </>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Divider />
          </Grid>
          {!saveOverWalls && (
            <Grid item container alignItems='center' spacing={1}>
              <Grid item>
                <ErrorOutline />
              </Grid>
              <Grid item style={{ marginBottom: '.5rem' }}>
                <Typography variant='subtitle2'>
                  Sharing walls will fail if the target user already has a wall
                  by the same name.
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      {!submitted && (
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant='contained'
            color='secondary'
          >
            Cancel
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={handleSubmit}
            disabled={!usersNotSelf || usersNotSelf.length === 0}
          >
            Submit
          </Button>
        </DialogActions>
      )}
      {submitted && (
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant='contained'
            color='primary'
          >
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ShareWallsDialog;
